import { useRef, useEffect } from 'react';

/**
 * Configuration for render debugging and performance warnings.
 */
export interface RenderDebugConfig {
  /** Enable/disable the hook entirely. Default: true in dev, false in prod */
  enabled?: boolean;
  /** Number of renders to trigger rapid render warning. Default: 5 */
  rapidRenderThreshold?: number;
  /** Time window (ms) for rapid render detection. Default: 100 */
  rapidRenderWindowMs?: number;
  /** Render count to trigger high render warning. Default: 50 */
  highRenderCountThreshold?: number;
  /** Warn when component re-renders with no tracked deps changed. Default: true */
  warnOnParentForced?: boolean;
  /** Warn when object/array reference changes but content is the same. Default: true */
  warnOnReferenceChanges?: boolean;
  /** Warn when a value oscillates back and forth. Default: true */
  warnOnOscillation?: boolean;
  /** Number of values to track for oscillation detection. Default: 4 */
  oscillationHistorySize?: number;
  /** Number of occurrences before warning about parent-forced renders. Default: 3 */
  parentForcedThreshold?: number;
  /** Number of occurrences before warning about reference changes per key. Default: 2 */
  referenceChangeThreshold?: number;
}

const defaultConfig: Required<RenderDebugConfig> = {
  enabled: process.env.NODE_ENV !== 'production',
  rapidRenderThreshold: 5,
  rapidRenderWindowMs: 100,
  highRenderCountThreshold: 50,
  warnOnParentForced: true,
  warnOnReferenceChanges: true,
  warnOnOscillation: true,
  oscillationHistorySize: 4,
  parentForcedThreshold: 3,
  referenceChangeThreshold: 2,
};

let globalConfig: Required<RenderDebugConfig> = { ...defaultConfig };

/**
 * Configure global settings for render debugging.
 * Call once at app startup (e.g., in main.tsx).
 */
export function configureRenderDebug(config: Partial<RenderDebugConfig>): void {
  globalConfig = { ...globalConfig, ...config };
}

// Track warnings that have already fired (to prevent spam)
const firedWarnings = new Set<string>();

/**
 * Deep equality check for objects/arrays.
 * Returns true if content is equal, false otherwise.
 * @internal Exported for testing only
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  if (a === null || b === null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);

  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(bObj, key)) return false;
    if (!deepEqual(aObj[key], bObj[key])) return false;
  }

  return true;
}

/**
 * Check if a value is an object or array (not null).
 */
function isObjectOrArray(value: unknown): value is object {
  return value !== null && typeof value === 'object';
}

/**
 * Detect oscillation pattern in value history.
 * Returns [valueA, valueB] if oscillating, null otherwise.
 * @internal Exported for testing only
 */
export function detectOscillation(history: unknown[]): [unknown, unknown] | null {
  if (history.length < 4) return null;

  // Check for A → B → A → B pattern
  const [a, b, c, d] = history.slice(-4);

  // For primitives, use strict equality
  // For objects, this won't catch oscillation (would need deep equality)
  if (a === c && b === d && a !== b) {
    return [a, b];
  }

  return null;
}

/**
 * Format a value for display in warnings.
 * @internal Exported for testing only
 */
export function formatValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'object') {
    try {
      const str = JSON.stringify(value);
      return str.length > 50 ? str.slice(0, 50) + '...' : str;
    } catch {
      return '[object]';
    }
  }
  return String(value);
}

/**
 * Fire a warning (only once per unique key).
 */
function warnOnce(key: string, message: string): void {
  if (firedWarnings.has(key)) return;
  firedWarnings.add(key);
  console.warn(message);
}

/**
 * Debug hook to log why a component re-rendered.
 * Logs the render count and which props/deps changed since the last render.
 * Also detects common performance anti-patterns and warns about them.
 *
 * Automatically disabled in production builds (NODE_ENV === 'production').
 * Safe to leave in place — no runtime cost in prod.
 *
 * Usage:
 *   useRenderDebug('MyComponent', { prop1, prop2, someState });
 */
export function useRenderDebug(componentName: string, deps: Record<string, unknown>): void {
  const renderCount = useRef(0);
  const prevDeps = useRef<Record<string, unknown>>({});
  const renderTimestamps = useRef<number[]>([]);
  const valueHistory = useRef<Record<string, unknown[]>>({});
  const highCountWarned = useRef(false);
  const parentForcedCount = useRef(0);
  const referenceChangeCounts = useRef<Record<string, number>>({});

  const config = globalConfig;

  // Early exit if disabled
  if (!config.enabled) return;

  renderCount.current += 1;
  const now = Date.now();
  renderTimestamps.current.push(now);

  // Keep only recent timestamps (last 10)
  if (renderTimestamps.current.length > 10) {
    renderTimestamps.current = renderTimestamps.current.slice(-10);
  }

  // Detect changed dependencies
  const changed: string[] = [];
  for (const key of Object.keys(deps)) {
    if (prevDeps.current[key] !== deps[key]) {
      changed.push(key);
    }
  }

  // Performance warnings (skip on initial render)
  if (renderCount.current > 1) {
    // 1. Rapid successive renders detection
    const timestamps = renderTimestamps.current;
    if (timestamps.length >= config.rapidRenderThreshold) {
      const recentTimestamps = timestamps.slice(-config.rapidRenderThreshold);
      const timespan = recentTimestamps[recentTimestamps.length - 1] - recentTimestamps[0];
      if (timespan <= config.rapidRenderWindowMs) {
        warnOnce(
          `${componentName}:rapid`,
          `⚠️ [perf] ${componentName}: Rapid re-renders detected (${config.rapidRenderThreshold} renders in ${timespan}ms) — possible infinite loop or cascade`
        );
      }
    }

    // 2. Parent-forced renders (no tracked deps changed)
    if (config.warnOnParentForced && changed.length === 0) {
      parentForcedCount.current += 1;
      if (parentForcedCount.current >= config.parentForcedThreshold) {
        warnOnce(
          `${componentName}:parent-forced`,
          `⚠️ [perf] ${componentName}: Re-rendered ${parentForcedCount.current}x with no tracked dependency changes — parent likely causing unnecessary renders`
        );
      }
    }

    // 3. Object/array reference changes with same content
    if (config.warnOnReferenceChanges) {
      for (const key of changed) {
        const prev = prevDeps.current[key];
        const next = deps[key];
        if (isObjectOrArray(prev) && isObjectOrArray(next)) {
          if (deepEqual(prev, next)) {
            referenceChangeCounts.current[key] = (referenceChangeCounts.current[key] ?? 0) + 1;
            if (referenceChangeCounts.current[key] >= config.referenceChangeThreshold) {
              warnOnce(
                `${componentName}:ref:${key}`,
                `⚠️ [perf] ${componentName}.${key}: New object reference with same content (${referenceChangeCounts.current[key]}x) — consider useMemo/useCallback`
              );
            }
          }
        }
      }
    }

    // 4. High render count threshold
    if (!highCountWarned.current && renderCount.current >= config.highRenderCountThreshold) {
      highCountWarned.current = true;
      console.warn(
        `⚠️ [perf] ${componentName}: Rendered ${config.highRenderCountThreshold}+ times this session — investigate render triggers`
      );
    }

    // 5. Oscillation detection (value toggling back and forth)
    if (config.warnOnOscillation) {
      for (const key of changed) {
        const value = deps[key];
        // Only track primitives for oscillation (objects would need deep comparison)
        if (!isObjectOrArray(value)) {
          if (!valueHistory.current[key]) {
            valueHistory.current[key] = [];
          }
          valueHistory.current[key].push(value);

          // Keep history limited
          if (valueHistory.current[key].length > config.oscillationHistorySize) {
            valueHistory.current[key] = valueHistory.current[key].slice(
              -config.oscillationHistorySize
            );
          }

          const oscillation = detectOscillation(valueHistory.current[key]);
          if (oscillation) {
            warnOnce(
              `${componentName}:oscillate:${key}`,
              `⚠️ [perf] ${componentName}.${key}: Value oscillating (${formatValue(oscillation[0])} ↔ ${formatValue(oscillation[1])}) — possible state thrashing`
            );
          }
        }
      }
    }
  }

  // Save current deps for next render comparison
  prevDeps.current = { ...deps };
}

/**
 * Debug hook to log when a context consumer re-renders due to context changes.
 * Wraps useRenderDebug but specifically tags as context-driven.
 *
 * Usage:
 *   const ctx = useOrganization();
 *   useContextDebug('MyComponent', ctx);
 */
export function useContextDebug(
  componentName: string,
  contextValue: Record<string, unknown>
): void {
  useRenderDebug(`${componentName}[ctx]`, contextValue);
}

/**
 * Reset fired warnings (useful for testing).
 */
export function resetRenderDebugWarnings(): void {
  firedWarnings.clear();
}

/**
 * Reset global config to defaults (useful for testing).
 * @internal Exported for testing only
 */
export function resetRenderDebugConfig(): void {
  globalConfig = { ...defaultConfig };
}
